/**
 * Playtests slice — prototype `ptToggleInt` (1999), `ptReport` (2000),
 * `ptClearSt` (2001), `ptUntrack` (2002), `ptDoCheck` (2004), `ptDoTrack`
 * (2013), `ptDoAddOff` (2021), `ptFromBrowse` (2032), and the Steam-link
 * controls inside `ptsy` (2213–2228).
 */

import type { StateCreator } from 'zustand';

import { ptFmt } from '../../lib/format.ts';
import { PT_BROWSE } from '../../lib/logic.ts';
import type { Playtest, PtBrowseRow, SteamLink } from '../../lib/types.ts';
import { mutate, storeApi } from '../mutate.ts';
import type { QdStore } from '../types.ts';

export interface PlaytestsSlice {
  pts: Playtest[];

  ptOf: (id: string) => Playtest | null;
  ptMySt: (id: string) => '' | 'in' | 'req';

  ptToggleInt: (id: string) => Promise<void>;
  ptReport: (id: string, s: 'in' | 'req') => Promise<void>;
  ptClearSt: (id: string) => Promise<void>;
  ptUntrack: (id: string) => Promise<void>;
  ptDoCheck: () => Promise<void>;
  ptDoTrack: () => Promise<void>;
  ptDoAddOff: () => Promise<void>;
  ptFromBrowse: (b: PtBrowseRow) => void;

  steamLinkStart: () => Promise<void>;
  steamUnlink: () => Promise<void>;
  steamSyncNow: () => Promise<void>;
}

const upd = (pts: Playtest[], id: string, fn: (e: Playtest) => Playtest): Playtest[] =>
  pts.map((e) => (e.id === id ? fn({ ...e, int: { ...e.int }, st: { ...e.st } }) : e));

export const createPlaytestsSlice: StateCreator<QdStore, [], [], PlaytestsSlice> = (set, get) => ({
  pts: [],

  ptOf: (id) => get().pts.find((e) => e.id === id) ?? null,
  ptMySt: (id) => {
    const s = get();
    const e = s.pts.find((x) => x.id === id);
    const st = e && s.session ? e.st[s.session] : undefined;
    return st ? st.s : '';
  },

  ptToggleInt: async (id) => {
    const s = get();
    const m = s.me();
    const e = s.ptOf(id);
    if (!m || !e) return;
    const on = !e.int[m.k];
    const now = Date.now();
    await mutate(storeApi(get, set), {
      key: 'ptInt:' + id + ':' + m.k,
      optimistic: (st) => ({
        pts: upd(st.pts, id, (x) => {
          if (on) x.int[m.k] = now;
          else delete x.int[m.k];
          return x;
        }),
      }),
      commit: () => s.repo.ptToggleInt(id),
      toast: on ? 'MARKED INTERESTED' : 'INTEREST CLEARED',
    });
  },

  ptReport: async (id, status) => {
    const s = get();
    const m = s.me();
    if (!m) return;
    const note = 'SELF-REPORTED ' + ptFmt(Date.now());
    await mutate(storeApi(get, set), {
      key: 'ptSt:' + id + ':' + m.k,
      optimistic: (st) => ({
        pts: upd(st.pts, id, (x) => {
          x.st[m.k] = { s: status, m: note };
          return x;
        }),
      }),
      commit: () => s.repo.ptReport(id, status),
      toast: status === 'in' ? 'MARKED: YOU GOT IN' : 'MARKED: SIGNED UP',
    });
  },

  ptClearSt: async (id) => {
    const s = get();
    const m = s.me();
    if (!m) return;
    await mutate(storeApi(get, set), {
      key: 'ptSt:' + id + ':' + m.k,
      optimistic: (st) => ({
        pts: upd(st.pts, id, (x) => {
          delete x.st[m.k];
          return x;
        }),
      }),
      commit: () => s.repo.ptClearSt(id),
      toast: 'STATUS CLEARED',
    });
  },

  /**
   * PORTING-NOTES D-04: an `auto` row is muted for me only, anything else is
   * removed crew-wide. Either way the row leaves *my* list, so the optimistic
   * patch is the same and only the toast differs.
   *
   * This call itself is unguarded — the gate lives at the caller, because only
   * the tracked sheet exposes the destructive half to a stray tap: it arms the
   * non-auto (crew-wide) `✕` through `ptRmArm` and calls this on the second tap
   * (docs/porting/P4b.md §7). The patch clears that arm so a confirmed untrack
   * cannot leave a removed row's id sitting armed.
   */
  ptUntrack: async (id) => {
    const s = get();
    const e = s.ptOf(id);
    if (!e) return;
    await mutate(storeApi(get, set), {
      key: 'ptUntrack:' + id,
      optimistic: (st) => ({ pts: st.pts.filter((x) => x.id !== id), ptDet: '', ptRmArm: '' }),
      commit: () => s.repo.ptUntrack(id),
      toast: e.auto ? 'MUTED FOR YOU' : 'UNTRACKED',
    });
  },

  /**
   * P6 F-2: this is a real round trip (~0.5–2 s uncached), so it carries the
   * P3e-03 busy flag. The early return is the double-tap guard — the button is
   * a plain `div`, so a second tap would otherwise stack a second request.
   * Failure is an `ok:false` result (P6a-05/P6a-06), not a rejection; the
   * `finally` is protection against a repository that throws anyway.
   */
  ptDoCheck: async () => {
    const s = get();
    if (s.ptAddChecking) return;
    set({ ptAddChecking: true });
    try {
      const res = await s.repo.ptDoCheck(s.ptAddUrl);
      set({ ptAddChk: res, ...(res.ok ? { ptAddParty: 0 } : {}) });
    } finally {
      set({ ptAddChecking: false });
    }
  },

  ptDoTrack: async () => {
    const s = get();
    if (s.ptTracking) return;
    const c = s.ptAddChk;
    if (!c || !c.ok) {
      s.toast('PASTE A LINK AND HIT CHECK FIRST', true);
      return;
    }
    if (!s.ptAddParty) {
      s.toast('PARTY SIZE REQUIRED', true);
      return;
    }
    set({ ptTracking: true });
    try {
      const created = await s.repo.ptDoTrack({
        appid: c.appid,
        name: c.name,
        dev: c.dev,
        hue: c.hue,
        party: s.ptAddParty,
        // P6 F-3: the row's status/foot/dates_src come from what the check
        // actually saw on the store page, not from a hardcoded optimism.
        widget: c.widget,
        playtestAppid: c.playtestAppid,
      });
      set((st) => ({
        pts: [...st.pts, created],
        ptAddOpen: false,
        ptAddUrl: '',
        ptAddChk: null,
        ptAddParty: 0,
      }));
      s.toast('TRACKING ' + created.name.toUpperCase() + ' FOR EVERYONE');
    } catch {
      s.toast("✕ COULDN'T TRACK THAT — TRY AGAIN", true);
    } finally {
      set({ ptTracking: false });
    }
  },

  ptDoAddOff: async () => {
    const s = get();
    if (s.ptTracking) return;
    const name = s.ptOffName.trim();
    const url = s.ptOffUrl.trim();
    if (!name || !url) {
      s.toast('NAME + OFFICIAL SIGNUP URL REQUIRED', true);
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      s.toast('URL MUST BE A FULL LINK (HTTPS://…)', true);
      return;
    }
    if (!s.ptAddParty) {
      s.toast('PARTY SIZE REQUIRED', true);
      return;
    }
    set({ ptTracking: true });
    try {
      const created = await s.repo.ptDoAddOff({
        name,
        url,
        start: s.ptOffStart,
        end: s.ptOffEnd,
        party: s.ptAddParty,
      });
      set((st) => ({
        pts: [...st.pts, created],
        ptAddOpen: false,
        ptOffName: '',
        ptOffUrl: '',
        ptOffStart: '',
        ptOffEnd: '',
        ptAddParty: 0,
      }));
      s.toast('TRACKING ' + name.toUpperCase() + ' FOR EVERYONE');
    } catch {
      s.toast('THAT URL DOES NOT PARSE', true);
    } finally {
      set({ ptTracking: false });
    }
  },

  /**
   * Browse row → the add sheet, pre-checked (prototype line 2032).
   *
   * P6 F-2/F-5: `PT_BROWSE` is four hardcoded SAMPLE rows and stays that way,
   * but the prototype's pre-check synthesised a result from them — including the
   * fabricated `· PLAYTEST APP #' + (appid + 1)`. Minting a `widget` verdict out
   * of a sample row would put the same lie one level deeper, where `ptDoTrack`
   * would write it into `dates_src`. So the row still opens the sheet
   * pre-filled and still arrives pre-checked — the check is just the real one
   * now, against the real store page.
   */
  ptFromBrowse: (b) => {
    set({
      ptBrowseOpen: false,
      ptAddOpen: true,
      ptAddTab: 'steam',
      ptAddUrl: b.appid ? 'https://store.steampowered.com/app/' + b.appid + '/' : '',
      ptAddChk: null,
      ptAddChecking: false,
      ptAddParty: 0,
    });
    if (b.appid) void get().ptDoCheck();
  },

  /**
   * P4b-03: the redirect URL was thrown away.
   *
   * `repo.steamLinkStart()` returns the URL the caller must send the member to
   * ("The caller performs the navigation." — SupabaseRepository), and against
   * Supabase that is Steam's own OpenID sign-in page. The old body discarded
   * it, refetched a `steam_links` row that of course did not exist yet, and
   * then toasted `STEAM LINKED — LIBRARY CHECKED · ALL CURRENT` over a `null`
   * link: the exact §12.3-class lie this port exists to remove. Linking was
   * simply unreachable in Supabase mode.
   *
   * LocalRepository links in place and hands back the page you are already on,
   * so the two modes are told apart by asking whether a link row now exists —
   * not by branching on the repository type.
   */
  steamLinkStart: async () => {
    const s = get();
    // Two presses opened TWO Steam sign-in tabs, or — once the popup blocker
    // ate the second — navigated the whole app away to Steam mid-session.
    if (s.steamBusy) return;
    set({ steamBusy: true });
    try {
      const { redirectUrl } = await s.repo.steamLinkStart(window.location.href);
      const link = await s.repo.fetchSteamLink(s.userId);
      if (link?.linked) {
        set({ steamLink: link });
        s.freshOk();
        s.toast('STEAM LINKED — LIBRARY CHECKED · ALL CURRENT');
        return;
      }
      // Not linked yet: the hand-off is the whole point. `↗` in this design means
      // "new tab" (PtBrowseSheet, PtDetailSheet, Detail), and the button that
      // calls this is `SIGN IN THROUGH STEAM ↗`.
      const win = typeof window !== 'undefined' ? window.open(redirectUrl, '_blank', 'noopener') : null;
      if (!win && typeof window !== 'undefined') window.location.assign(redirectUrl);
      s.toast('OPENING STEAM SIGN-IN…');
    } catch {
      s.toast("✕ COULDN'T REACH STEAM — TRY AGAIN", true);
    } finally {
      set({ steamBusy: false });
    }
  },

  steamUnlink: async () => {
    const s = get();
    if (s.steamBusy) return;
    set({ steamBusy: true });
    try {
      await s.repo.steamUnlink();
      // `myLib` and the wishlist read go with the link. Both describe an
      // account that can no longer be asked about, and the BACKLOG's tabs must
      // fall back to the unlinked copy rather than keep filtering on the last
      // answer from before the unlink. `wishAsked` resets too, so re-linking in
      // the same session reads the wishlist again instead of staying pending.
      set({ steamLink: null, myLib: {}, wishIds: null, wishClosed: false, wishAsked: false });
      s.toast('STEAM UNLINKED');
    } catch {
      s.toast("✕ COULDN'T UNLINK STEAM — TRY AGAIN", true);
    } finally {
      set({ steamBusy: false });
    }
  },

  steamSyncNow: async () => {
    const s = get();
    s.freshBusy();
    const res = await s.repo.steamSyncNow();
    if (res.ok) {
      const link: SteamLink | null = s.steamLink
        ? { ...s.steamLink, lastSyncAt: res.checkedAt, profilePublic: res.profilePublic }
        : null;
      // A CHECK NOW is exactly the event that can change what `myLib` holds:
      // `op=self` rewrites `steam_owned_apps` with new appids and new playtime.
      // Dropping the cache re-asks on the next BACKLOG render, so a game bought
      // or played since the last read stops being filed under UNOWNED.
      set({ steamLink: link, myLib: {} });
      s.freshOk();
      // Not `LIBRARY + STORE WIDGETS`: `steam-sync op=self` calls GetOwnedGames
      // and nothing else. The widget scrape is `playtest-scan op=scan`, which is
      // a cron this button cannot reach — claiming it ran here is what made
      // CHECK NOW look like it should have noticed a request.
      s.toast('CHECKED — STEAM LIBRARY CURRENT');
    } else {
      s.freshFail('UPSTREAM_STATUS');
      s.toast('✕ COULD NOT CHECK — RETRYING', true);
    }
  },
});

export { PT_BROWSE };
